import { daysBetween, isDateString } from './vacationRules';

/**
 * PRX-130 — the Prescriptions tab's rows (the all-prescriptions read, one row
 * per Rx chain, its newest fill) as the vacation supply calculator's inputs.
 *
 * Per day = disp qty ÷ days supply. On hand is an estimate from the last fill
 * (disp qty less what was taken since); staff can change it. Anything missing
 * stays null and the engine says "Can't calculate".
 */
export interface VacationRxRow {
  rxNumber: string;
  drugName: string;
  lastFillDate: string | null;
  dispQty: number | null;
  daysSupply: number | null;
  perDay: number | null;
  estimatedOnHandQty: number | null;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

function positiveNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dateOnly(value: unknown): string | null {
  if (value == null) return null;
  const head = String(value).slice(0, 10);
  return isDateString(head) ? head : null;
}

export function estimateOnHandQty(
  input: { lastFillDate: string | null; dispQty: number | null; perDay: number | null },
  today: string,
): number | null {
  const { lastFillDate, dispQty, perDay } = input;
  if (!lastFillDate || dispQty == null || perDay == null) return null;
  const taken = Math.max(0, daysBetween(lastFillDate, today)) * perDay;
  return round2(Math.max(0, dispQty - taken));
}

export function vacationRowsFromRx(prescriptions: any[], today: string): VacationRxRow[] {
  return (prescriptions || [])
    .filter((rx) => rx?.RxNum != null && rx.rxStatusWord !== 'deleted')
    .map((rx) => {
      const dispQty = positiveNumber(rx.DispQty);
      const daysSupply = positiveNumber(rx.DaysSupply);
      const lastFillDate = dateOnly(rx.FillDate);
      const perDay = dispQty != null && daysSupply != null ? dispQty / daysSupply : null;
      return {
        rxNumber: String(rx.RxNum),
        drugName: String(rx.BrandName || rx.GenericName || '').trim(),
        lastFillDate,
        dispQty,
        daysSupply,
        perDay,
        estimatedOnHandQty: estimateOnHandQty({ lastFillDate, dispQty, perDay }, today),
      };
    });
}
